import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Phone, Check, Search, Globe } from 'lucide-react';

// Liste complète des pays avec indicatifs
const COUNTRIES = [
  // France et DOM-TOM (priorité)
  { code: 'FR', dialCode: '+33', name: 'France', length: 9 },
  { code: 'GP', dialCode: '+590', name: 'Guadeloupe 🌴', length: 9 },
  { code: 'MQ', dialCode: '+596', name: 'Martinique 🌴', length: 9 },
  { code: 'GF', dialCode: '+594', name: 'Guyane française', length: 9 },
  { code: 'RE', dialCode: '+262', name: 'La Réunion', length: 9 },
  { code: 'YT', dialCode: '+262', name: 'Mayotte', length: 9 },
  { code: 'NC', dialCode: '+687', name: 'Nouvelle-Calédonie', length: 6 },
  { code: 'PF', dialCode: '+689', name: 'Polynésie française', length: 6 },
  { code: 'PM', dialCode: '+508', name: 'Saint-Pierre-et-Miquelon', length: 6 },
  { code: 'WF', dialCode: '+681', name: 'Wallis-et-Futuna', length: 6 },
  { code: 'BL', dialCode: '+590', name: 'Saint-Barthélemy', length: 9 },
  { code: 'MF', dialCode: '+590', name: 'Saint-Martin', length: 9 },
  
  // Europe
  { code: 'BE', dialCode: '+32', name: 'Belgique', length: 9 },
  { code: 'CH', dialCode: '+41', name: 'Suisse', length: 9 },
  { code: 'LU', dialCode: '+352', name: 'Luxembourg', length: 9 },
  { code: 'MC', dialCode: '+377', name: 'Monaco', length: 8 },
  { code: 'DE', dialCode: '+49', name: 'Allemagne', length: 11 },
  { code: 'ES', dialCode: '+34', name: 'Espagne', length: 9 },
  { code: 'IT', dialCode: '+39', name: 'Italie', length: 10 },
  { code: 'PT', dialCode: '+351', name: 'Portugal', length: 9 },
  { code: 'GB', dialCode: '+44', name: 'Royaume-Uni', length: 10 },
  { code: 'NL', dialCode: '+31', name: 'Pays-Bas', length: 9 },
  
  // Afrique
  { code: 'MA', dialCode: '+212', name: 'Maroc', length: 9 },
  { code: 'DZ', dialCode: '+213', name: 'Algérie', length: 9 },
  { code: 'TN', dialCode: '+216', name: 'Tunisie', length: 8 },
  { code: 'SN', dialCode: '+221', name: 'Sénégal', length: 9 },
  { code: 'CI', dialCode: '+225', name: "Côte d'Ivoire", length: 10 },
  { code: 'CM', dialCode: '+237', name: 'Cameroun', length: 9 },
  { code: 'MG', dialCode: '+261', name: 'Madagascar', length: 9 },
  
  // Amérique
  { code: 'US', dialCode: '+1', name: 'États-Unis', length: 10 },
  { code: 'CA', dialCode: '+1', name: 'Canada', length: 10 },
  { code: 'HT', dialCode: '+509', name: 'Haïti', length: 8 },
  { code: 'BR', dialCode: '+55', name: 'Brésil', length: 11 },
];

// URL pour les drapeaux
const getFlagUrl = (countryCode) => {
  return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
};

export default function PhoneInput({
  id,
  value = '',
  onChange = () => {},
  placeholder = 'Numéro de téléphone',
  className = '',
  label = 'Téléphone',
  showLabel = true,
  defaultCountryCode = 'FR',
  territory = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    // Trouver le pays par défaut basé sur le territory ou defaultCountryCode
    if (territory) {
      const territoryToCode = {
        'Guadeloupe': 'GP',
        'Martinique': 'MQ',
        'Guyane': 'GF',
        'Guyane française': 'GF',
        'La Réunion': 'RE',
        'Réunion': 'RE',
        'Mayotte': 'YT',
        'Nouvelle-Calédonie': 'NC',
        'Polynésie française': 'PF',
        'Polynésie': 'PF',
        'Saint-Pierre-et-Miquelon': 'PM',
        'Wallis-et-Futuna': 'WF',
        'Saint-Barthélemy': 'BL',
        'Saint-Martin': 'MF',
        'France': 'FR',
        'France métropolitaine': 'FR'
      };
      const code = territoryToCode[territory] || defaultCountryCode;
      return COUNTRIES.find(c => c.code === code) || COUNTRIES[0];
    }
    return COUNTRIES.find(c => c.code === defaultCountryCode) || COUNTRIES[0];
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  // Parse initial value et détecter le pays
  useEffect(() => {
    if (value) {
      const country = COUNTRIES.find(c => value.startsWith(c.dialCode));
      if (country && country.code !== selectedCountry.code) {
        setSelectedCountry(country);
        const num = value.replace(country.dialCode, '').replace(/\s/g, '');
        setPhoneNumber(num);
      }
    }
  }, [value, selectedCountry.code]);

  // Mettre à jour le pays quand le territory change
  useEffect(() => {
    if (territory) {
      const territoryToCode = {
        'Guadeloupe': 'GP',
        'Martinique': 'MQ',
        'Guyane': 'GF',
        'Guyane française': 'GF',
        'La Réunion': 'RE',
        'Réunion': 'RE',
        'Mayotte': 'YT',
        'Nouvelle-Calédonie': 'NC',
        'Polynésie française': 'PF',
        'Polynésie': 'PF',
        'Saint-Pierre-et-Miquelon': 'PM',
        'Wallis-et-Futuna': 'WF',
        'Saint-Barthélemy': 'BL',
        'Saint-Martin': 'MF',
        'France': 'FR',
        'France métropolitaine': 'FR'
      };
      const code = territoryToCode[territory];
      if (code) {
        const newCountry = COUNTRIES.find(c => c.code === code);
        if (newCountry && newCountry.code !== selectedCountry.code) {
          setSelectedCountry(newCountry);
          // Mettre à jour la valeur avec le nouvel indicatif si un numéro existe
          if (phoneNumber) {
            onChange(`${newCountry.dialCode}${phoneNumber}`);
          }
        }
      }
    }
  }, [territory, selectedCountry.code, phoneNumber, onChange]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter countries
  const filteredCountries = COUNTRIES.filter(country => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return country.name.toLowerCase().includes(query) || 
           country.dialCode.includes(query) ||
           country.code.toLowerCase().includes(query);
  });

  // Handle phone change
  const handlePhoneChange = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    const trimmed = input.slice(0, selectedCountry.length);
    setPhoneNumber(trimmed);
    onChange(trimmed ? `${selectedCountry.dialCode}${trimmed}` : '');
  };

  // Handle country selection
  const selectCountry = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery('');
    if (phoneNumber) {
      onChange(`${country.dialCode}${phoneNumber}`);
    }
  };

  return (
    <div className={`space-y-1 ${className}`} ref={containerRef}>
      {showLabel && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          <Phone className="inline w-4 h-4 mr-1" />
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="flex border-2 border-gray-200 rounded-lg overflow-hidden focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
          {/* Country Selector */}
          <button
            type="button"
            aria-label={`Choisir l’indicatif téléphonique : ${selectedCountry.name}`}
            aria-expanded={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-r border-gray-200 hover:bg-gray-100 transition-colors min-w-[120px]"
          >
            <img 
              src={getFlagUrl(selectedCountry.code)} 
              alt={selectedCountry.name}
              className="w-6 h-4 object-cover rounded"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-sm font-medium text-gray-700">{selectedCountry.dialCode}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Phone Input */}
          <input
            id={id}
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            className="min-w-0 flex-1 px-3 py-2.5 outline-none text-gray-900"
          />
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div 
            className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden"
            style={{ zIndex: 99999 }}
          >
            {/* Search */}
            <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un pays..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Country List */}
            <div className="overflow-y-auto max-h-60">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country, index) => (
                  <button
                    key={`${country.code}-${index}`}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectCountry(country);
                    }}
                    className={`flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-blue-50 transition-colors ${
                      selectedCountry.code === country.code && selectedCountry.dialCode === country.dialCode
                        ? 'bg-blue-50 border-l-4 border-blue-500'
                        : ''
                    }`}
                  >
                    <img 
                      src={getFlagUrl(country.code)} 
                      alt={country.name}
                      className="w-6 h-4 object-cover rounded"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="flex-1 text-sm text-gray-700">{country.name}</span>
                    <span className="text-sm text-gray-500 font-mono">{country.dialCode}</span>
                    {selectedCountry.code === country.code && selectedCountry.dialCode === country.dialCode && (
                      <Check className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun pays trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { COUNTRIES, getFlagUrl };
